import { BookOpen, Globe, Hash, Mail, Megaphone, MessageCircle, Plus, Sparkles, Webhook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import type { ContentConnectorType } from "@/lib/composerCore";

type Props = {
  canManage: boolean;
  onRefresh: () => void;
  onCreate: (type: ContentConnectorType) => void;
  readyDurationSec: number;
  setReadyDurationSec: (n: number) => void;
};

export const ConnectorManagerHeader = ({
  canManage,
  onRefresh,
  onCreate,
  readyDurationSec,
  setReadyDurationSec,
}: Props) => (
  <>
    <CardHeader className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
      <div>
        <CardTitle className="flex items-center gap-2">
          <Webhook className="h-5 w-5 text-primary" /> Publishing connectors
        </CardTitle>
        <CardDescription>
          Send prepared publications to a webhook, by email, or to your Kit (formerly ConvertKit) account. Secrets are stored encrypted.
        </CardDescription>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={!canManage}>
          Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={() => onCreate("kit")} disabled={!canManage}>
          <Sparkles className="h-4 w-4" /> New Kit
        </Button>
        <Button variant="outline" size="sm" onClick={() => onCreate("bento")} disabled={!canManage}>
          <Megaphone className="h-4 w-4" /> New Bento
        </Button>
        <Button variant="outline" size="sm" onClick={() => onCreate("wordpress")} disabled={!canManage}>
          <Globe className="h-4 w-4" /> New WordPress.com
        </Button>
        <Button variant="outline" size="sm" onClick={() => onCreate("wordpress_self_hosted")} disabled={!canManage}>
          <Globe className="h-4 w-4" /> New WordPress (self-hosted)
        </Button>
        <Button variant="outline" size="sm" onClick={() => onCreate("ghost")} disabled={!canManage}>
          <Sparkles className="h-4 w-4" /> New Ghost
        </Button>
        <Button variant="outline" size="sm" onClick={() => onCreate("substack_unofficial")} disabled={!canManage}>
          <BookOpen className="h-4 w-4" /> New Substack
        </Button>
        <Button variant="outline" size="sm" onClick={() => onCreate("beehiiv")} disabled={!canManage}>
          <Megaphone className="h-4 w-4" /> New Beehiiv
        </Button>
        <Button variant="outline" size="sm" onClick={() => onCreate("buttondown")} disabled={!canManage}>
          <Mail className="h-4 w-4" /> New Buttondown
        </Button>
        {/* New Bluesky / New Mastodon buttons removed 2026-07-03. */}
        <Button variant="outline" size="sm" onClick={() => onCreate("email")} disabled={!canManage}>
          <Mail className="h-4 w-4" /> New email
        </Button>
        <Button size="sm" onClick={() => onCreate("webhook")} disabled={!canManage}>
          <Plus className="h-4 w-4" /> New webhook
        </Button>
      </div>
    </CardHeader>
    <div className="flex flex-wrap items-center justify-end gap-2 px-6 pb-2 text-xs text-muted-foreground">
      <Label htmlFor="connector-ready-duration" className="text-xs font-normal text-muted-foreground">
        "Editor ready" stays for
      </Label>
      <select
        id="connector-ready-duration"
        value={readyDurationSec}
        onChange={(e) => setReadyDurationSec(Number(e.target.value))}
        className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        aria-label="Duration the Editor ready indicator stays visible"
      >
        <option value={2}>2 seconds</option>
        <option value={4}>4 seconds (default)</option>
        <option value={8}>8 seconds</option>
        <option value={15}>15 seconds</option>
        <option value={30}>30 seconds</option>
        <option value={0}>Keep visible until dismissed</option>
      </select>
    </div>
  </>
);
